<script setup lang="ts">
import { ref, computed, reactive, onMounted, watch } from 'vue'
import { gsap } from 'gsap'

const props = defineProps<{
  id: number
  initialScale?: number
  initialHue?: number
}>()

const emit = defineEmits<{
  mutate: [id: number, mutationLevel: number]
}>()

// 鱼体变异参数
const mutationLevel = ref(0)
const hue = ref(props.initialHue || Math.random() * 360)
const scale = ref(props.initialScale || 0.8 + Math.random() * 0.4)
const skew = ref(0)
const rotation = ref(Math.random() * 360)
const position = reactive({
  x: 100 + Math.random() * (window.innerWidth - 200),
  y: 100 + Math.random() * (window.innerHeight - 200),
})

// 鱼体器官系统
interface Organ {
  type: string
  x: number
  y: number
  scale: number
  rotation: number
  hue: number
}

const organs = ref<Organ[]>([
  {
    type: 'eye',
    x: 15,
    y: -10,
    scale: 1,
    rotation: 0,
    hue: (hue.value + 40) % 360,
  },
  {
    type: 'fin',
    x: -30,
    y: 0,
    scale: 1,
    rotation: 180,
    hue: (hue.value + 20) % 360,
  },
  {
    type: 'tail',
    x: -40,
    y: 0,
    scale: 1,
    rotation: 180,
    hue: (hue.value - 20 + 360) % 360,
  },
])

// 生成鱼体样式
const fishStyle = computed(() => ({
  '--skin-color': `hsl(${hue.value}, 70%, 50%)`,
  transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation.value}deg) skew(${skew.value}deg) scale(${scale.value})`,
  filter:
    mutationLevel.value > 5
      ? `hue-rotate(${Math.sin(Date.now() / 1000) * 30}deg) contrast(${1 + mutationLevel.value / 10})`
      : '',
}))

// 获取器官样式
const organStyle = (organ: Organ) => ({
  transform: `translate(${organ.x}px, ${organ.y}px) rotate(${organ.rotation}deg) scale(${organ.scale})`,
  '--organ-color': `hsl(${organ.hue}, 70%, 50%)`,
})

// 随机移动
const moveRandomly = () => {
  const targetX = 100 + Math.random() * (window.innerWidth - 200)
  const targetY = 100 + Math.random() * (window.innerHeight - 200)
  const targetRotation = rotation.value + (Math.random() > 0.5 ? 1 : -1) * (30 + Math.random() * 60)

  gsap.to(position, {
    x: targetX,
    y: targetY,
    duration: 2 + Math.random() * 3,
    ease: 'power2.inOut',
    onComplete: () => {
      if (Math.random() > 0.3) {
        setTimeout(moveRandomly, 1000 + Math.random() * 3000)
      }
    },
  })

  gsap.to(rotation, {
    value: targetRotation,
    duration: 2,
    ease: 'power1.inOut',
  })
}

// 基于变异等级应用拓扑变化
watch(mutationLevel, (newLevel) => {
  if (newLevel >= 3 && organs.value.length < 6) {
    // 添加新器官
    organs.value.push({
      type: ['eye', 'tentacle', 'fin', 'crystal'][Math.floor(Math.random() * 4)],
      x: (Math.random() - 0.5) * 60,
      y: (Math.random() - 0.5) * 40,
      scale: 0.7 + Math.random() * 0.6,
      rotation: Math.random() * 360,
      hue: (hue.value + Math.random() * 120) % 360,
    })
  }

  // 变形现有器官
  organs.value.forEach((organ) => {
    gsap.to(organ, {
      scale: 0.7 + Math.random() * 0.6 * (1 + newLevel / 10),
      rotation: organ.rotation + (Math.random() - 0.5) * 60,
      hue: (organ.hue + 20 * Math.random()) % 360,
      duration: 0.5,
      ease: 'elastic.out(1, 0.3)',
    })
  })

  // 更改整体形态
  gsap.to(skew, {
    value: (Math.random() - 0.5) * newLevel * 3,
    duration: 0.5,
    ease: 'back.out(2)',
  })
})

// 被喂食时触发变异
const feed = (mutationStrength: number) => {
  const oldLevel = mutationLevel.value
  mutationLevel.value += mutationStrength

  // 视觉反馈
  gsap.to(scale, {
    value: scale.value * 1.2,
    duration: 0.2,
    ease: 'power2.in',
    onComplete: () => {
      gsap.to(scale, {
        value: scale.value / 1.2,
        duration: 0.5,
        ease: 'elastic.out(1, 0.3)',
      })
    },
  })

  // 颜色变化
  gsap.to(hue, {
    value: (hue.value + mutationStrength * 15) % 360,
    duration: 0.5,
    ease: 'power1.inOut',
  })

  emit('mutate', props.id, mutationLevel.value - oldLevel)
}

// 检测是否被喂食
const checkFeedingHit = (foodX: number, foodY: number) => {
  const fishSize = 60 * scale.value
  const dx = position.x - foodX
  const dy = position.y - foodY
  const distance = Math.sqrt(dx * dx + dy * dy)

  if (distance < fishSize) {
    feed(1 + Math.random() * 2)
    return true
  }
  return false
}

// 暴露方法给父组件
defineExpose({
  checkFeedingHit,
  feed,
  position,
})

onMounted(() => {
  moveRandomly()
})
</script>

<template>
  <div class="quantum-fish" :style="fishStyle">
    <div class="fish-body"></div>
    <div
      v-for="(organ, index) in organs"
      :key="index"
      :class="['organ', organ.type]"
      :style="organStyle(organ)"
    ></div>
  </div>
</template>

<style scoped>
.quantum-fish {
  position: absolute;
  width: 80px;
  height: 40px;
  transition: filter 0.5s ease;
  will-change: transform;
}

.fish-body {
  position: absolute;
  width: 100%;
  height: 100%;
  background-color: var(--skin-color);
  border-radius: 40% 60% 60% 40% / 60% 40% 60% 40%;
  box-shadow: 0 0 20px rgba(var(--skin-color), 0.3);
  overflow: hidden;
}

.organ {
  position: absolute;
  top: 50%;
  left: 50%;
  transform-origin: center center;
}

.eye {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: white;
  border: 2px solid black;
}

.eye::after {
  content: '';
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: black;
  border-radius: 50%;
  top: 3px;
  left: 3px;
}

.fin {
  width: 20px;
  height: 15px;
  background-color: var(--organ-color);
  border-radius: 50% 0 50% 50%;
  opacity: 0.8;
}

.tail {
  width: 20px;
  height: 20px;
  background-color: var(--organ-color);
  clip-path: polygon(0 0, 100% 50%, 0 100%);
  opacity: 0.8;
}

.tentacle {
  width: 30px;
  height: 8px;
  background-color: var(--organ-color);
  border-radius: 40% 60% 60% 40% / 60% 40% 60% 40%;
  opacity: 0.8;
}

.crystal {
  width: 12px;
  height: 12px;
  background-color: var(--organ-color);
  clip-path: polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%);
  opacity: 0.9;
  border: 1px solid rgba(255, 255, 255, 0.5);
}

/* 变异等级样式增强 */
.quantum-fish[style*='contrast(1.5)'] .fish-body {
  mix-blend-mode: exclusion;
}

.quantum-fish[style*='contrast(1.6)'] .fish-body {
  animation: pulse 2s infinite alternate;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
  }
  100% {
    box-shadow: 0 0 30px rgba(255, 255, 255, 0.7);
  }
}
</style>
